import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function SettingsPage() {
  const { user, updateProfile, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [ack, setAck] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user && typeof user === "object") {
      setName(user.display_name || "");
      setAck(!!user.disclaimer_acknowledged);
    }
  }, [user]);

  if (user && typeof user !== "object") {
    return null;
  }

  const save = async () => {
    setErr("");
    setMsg("");
    const res = await updateProfile({ display_name: name, disclaimer_acknowledged: ack });
    if (res.ok) setMsg("Saved.");
    else setErr(res.error || "Could not save.");
  };

  const wipe = async () => {
    if (
      !window.confirm(
        "Delete your account? All saved reviews will be permanently removed."
      )
    )
      return;
    await deleteAccount();
    navigate("/");
  };

  return (
    <section
      data-testid="settings-page"
      className="max-w-[820px] mx-auto px-6 md:px-10 py-12"
    >
      <div className="eyebrow mb-4">Settings</div>
      <h1 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight">
        Your account.
      </h1>

      <div className="mt-10 paper-card">
        <div className="eyebrow mb-4">Profile</div>
        <label className="block">
          <div className="text-xs font-mono-plex uppercase tracking-widest text-ink-50 mb-1.5">
            Email
          </div>
          <input
            data-testid="settings-email"
            disabled
            value={user?.email || ""}
            className="w-full border border-ink-10 px-3 py-2.5 bg-[rgba(10,10,10,0.03)] outline-none text-sm text-ink-70"
          />
        </label>

        <label className="block mt-5">
          <div className="text-xs font-mono-plex uppercase tracking-widest text-ink-50 mb-1.5">
            Display name
          </div>
          <input
            data-testid="settings-display-name"
            className="w-full border border-ink-20 px-3 py-2.5 bg-white outline-none focus-ring text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label className="flex items-start gap-3 mt-6">
          <input
            data-testid="settings-ack-checkbox"
            type="checkbox"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            className="mt-1 accent-[var(--forest)]"
          />
          <span className="text-sm text-ink-70">
            I understand PDF Review is document review assistance — not legal, tax, or
            professional advice.
          </span>
        </label>

        <div className="mt-6 flex items-center gap-3">
          <button
            data-testid="settings-save-btn"
            onClick={save}
            className="btn-primary focus-ring"
          >
            Save changes
          </button>
          {msg && <span className="text-sm text-forest">{msg}</span>}
          {err && (
            <span className="text-sm text-[var(--severity-red-text)]">{err}</span>
          )}
        </div>
      </div>

      <div
        className="mt-10 paper-card border-[var(--severity-red-border)]"
        style={{ borderColor: "var(--severity-red-border)" }}
      >
        <div className="eyebrow mb-3" style={{ color: "var(--severity-red-text)" }}>
          Danger zone
        </div>
        <div className="text-sm text-ink-70 mb-5">
          Delete your account and all saved reviews. This cannot be undone.
        </div>
        <button
          data-testid="settings-delete-account"
          onClick={wipe}
          className="btn-outline focus-ring"
          style={{
            borderColor: "var(--severity-red-text)",
            color: "var(--severity-red-text)",
          }}
        >
          Delete account
        </button>
      </div>
    </section>
  );
}
